import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { toast } from 'sonner';
import { FileType } from '@/types';
import { ChatContainer } from './ChatContainer';
import { ChatInput } from './ChatInput';
import { ScrollableMessageArea } from './ScrollableMessageArea';
import { ChatMessage } from './ChatMessage';
import { ToolCallDisplay } from './ToolCallDisplay';
import { useAutoScroll } from '../hooks/useAutoScroll';
import {
  useStreamingChat,
  StreamingChatMessage,
  StreamingChatOptions,
} from '../hooks/useStreamingChat';

export interface StreamingChatContainerProps {
  // Mode configuration
  mode: 'single' | 'multi' | 'groq';

  // File context
  teamId: string;
  fileId?: string; // For single mode
  fileIds?: string[]; // For multi/groq mode (optional if autoSearch is enabled)
  cachedFiles?: FileType[]; // For displaying file chips

  // Session management
  sessionId?: string | null;
  onSessionUpdate?: (sessionId: string) => void;

  // Conversation state
  conversationHistory: StreamingChatMessage[];
  onConversationUpdate?: (messages: StreamingChatMessage[]) => void;

  // Media analysis options (for single mode)
  mediaAnalysisOptions?: any;

  // Auto-search configuration
  searchLimit?: number; // Number of files to auto-select when searching (default: 5)

  // Tool configuration (for groq mode)
  enabledTools?: string[]; // Tools the LLM can use in groq mode
  model?: string; // Model to use in groq mode (default: kimi-k2)

  // UI configuration
  placeholder?: string;
  layout?: 'card' | 'fullscreen';
  isInPanel?: boolean;
  isInTab?: boolean;

  // Feature flags
  enableFileSelection?: boolean;
  enableHistory?: boolean;
  enableClear?: boolean;

  // Callbacks
  onClear?: () => void;
  onFileSelect?: (file: FileType) => void;
  onFileRemove?: (fileId: string) => void;
  onBeforeSubmit?: (question: string, options: StreamingChatOptions) => void;
  onFilesSelected?: (fileIds: string[]) => void; // Called when backend auto-selects files

  // Custom input component (optional)
  renderInput?: (props: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    isLoading: boolean;
    onStop: () => void;
    disabled: boolean;
  }) => React.ReactNode;
}

export interface StreamingChatContainerRef {
  clearChat: () => void;
  hasContent: () => boolean;
  focusInput: () => void;
  sendMessage: (message: string) => void;
}

export const StreamingChatContainer = forwardRef<
  StreamingChatContainerRef,
  StreamingChatContainerProps
>(
  (
    {
      mode,
      teamId,
      fileId,
      fileIds,
      sessionId,
      onSessionUpdate,
      conversationHistory,
      onConversationUpdate,
      mediaAnalysisOptions,
      searchLimit = 5,
      enabledTools,
      model,
      placeholder = 'Ask anything...',
      layout = 'card',
      isInPanel = false,
      isInTab = false,
      onClear,
      onBeforeSubmit,
      onFilesSelected,
      renderInput,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Auto-scroll hook
    const { containerRef, scrollAnchorRef } = useAutoScroll({
      dependencies: [conversationHistory.length],
      enabled: true,
      isStreaming: false, // Will be updated by streaming hook
    });

    // Use refs to store latest callback values without causing re-renders
    const conversationHistoryRef = useRef(conversationHistory);
    const onConversationUpdateRef = useRef(onConversationUpdate);
    const onSessionUpdateRef = useRef(onSessionUpdate);
    const onFilesSelectedRef = useRef(onFilesSelected);

    // Update refs on every render
    conversationHistoryRef.current = conversationHistory;
    onConversationUpdateRef.current = onConversationUpdate;
    onSessionUpdateRef.current = onSessionUpdate;
    onFilesSelectedRef.current = onFilesSelected;

    // Stable callbacks for streaming hook (never recreated)
    const handleMessageAdd = useCallback((message: StreamingChatMessage) => {
      if (onConversationUpdateRef.current) {
        onConversationUpdateRef.current([
          ...conversationHistoryRef.current,
          message,
        ]);
      }
    }, []);

    const handleMessageUpdate = useCallback((content: string) => {
      if (
        onConversationUpdateRef.current &&
        conversationHistoryRef.current.length > 0
      ) {
        const updatedHistory = [...conversationHistoryRef.current];
        const lastMessage = updatedHistory[updatedHistory.length - 1];
        if (lastMessage.role === 'assistant') {
          lastMessage.content = content;
          onConversationUpdateRef.current(updatedHistory);
        }
      }
    }, []);

    const handleSessionUpdate = useCallback((newSessionId: string) => {
      onSessionUpdateRef.current?.(newSessionId);
    }, []);

    const handleFilesSelected = useCallback((selectedFileIds: string[]) => {
      console.log(
        '[StreamingChatContainer] onFilesSelected triggered with:',
        selectedFileIds,
      );
      onFilesSelectedRef.current?.(selectedFileIds);
    }, []);

    const handleError = useCallback((error: Error) => {
      toast.error(
        error.message || 'Failed to process request. Please try again.',
      );
    }, []);

    const handleComplete = useCallback(() => {
      // Auto-scroll to bottom on completion
      scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Streaming chat hook
    const {
      isStreaming,
      toolCallSteps,
      isSearching,
      startStreaming,
      stopStreaming,
    } = useStreamingChat({
      onMessageAdd: handleMessageAdd,
      onMessageUpdate: handleMessageUpdate,
      onSessionUpdate: handleSessionUpdate,
      onFilesSelected: handleFilesSelected,
      onError: handleError,
      onComplete: handleComplete,
    });

    const handleSubmit = async (question?: string) => {
      const messageToSend = question || inputValue.trim();
      if (!messageToSend) return;

      // Clear input
      setInputValue('');

      console.log(
        '[StreamingChatContainer] handleSubmit - mode:',
        mode,
        'enabledTools:',
        enabledTools,
      );

      // Build streaming options
      const streamingOptions: StreamingChatOptions = {
        mode,
        teamId,
        sessionId,
      };

      if (mode === 'single') {
        if (!fileId) {
          toast.error('No file selected');
          return;
        }
        streamingOptions.fileId = fileId;
        streamingOptions.mediaAnalysisOptions = mediaAnalysisOptions;
      } else if (mode === 'groq') {
        // For groq mode, fileIds is optional - LLM can use tools to find files
        streamingOptions.fileIds = fileIds || [];
        streamingOptions.enabledTools = enabledTools;
        streamingOptions.model = model || 'kimi-k2';
      } else {
        // For multi mode, fileIds is optional - will auto-search if empty
        streamingOptions.fileIds = fileIds || [];
        streamingOptions.searchLimit = searchLimit;
      }

      // Call onBeforeSubmit callback for tracking
      onBeforeSubmit?.(messageToSend, streamingOptions);

      // Start streaming
      await startStreaming(messageToSend, streamingOptions);
    };

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        clearChat: () => {
          if (onClear) {
            onClear();
          } else {
            onConversationUpdate?.([]);
          }
        },
        hasContent: () => conversationHistory.length > 0,
        focusInput: () => {
          inputRef.current?.focus();
        },
        sendMessage: (message: string) => {
          handleSubmit(message);
        },
      }),
      [conversationHistory.length, onClear, onConversationUpdate, handleSubmit],
    );

    const renderMessages = () => {
      return (
        <ChatContainer.Messages scrollVariant="flex-reverse">
          <ScrollableMessageArea
            containerRef={containerRef}
            scrollAnchorRef={scrollAnchorRef}
          >
            {conversationHistory.map((message, index) => {
              const isLastMessage = index === conversationHistory.length - 1;
              const isStreamingAssistant =
                isLastMessage && message.role === 'assistant' && isStreaming;

              return (
                <React.Fragment key={index}>
                  <ChatMessage role={message.role} content={message.content} />
                  {/* Show tool call display while streaming (rendered after for flex-reverse) */}
                  {isStreamingAssistant && toolCallSteps.length > 0 && (
                    <ToolCallDisplay
                      thinkingText={
                        isSearching ? 'Searching your recordings...' : undefined
                      }
                      steps={toolCallSteps}
                      isVisible
                      defaultExpanded
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ScrollableMessageArea>
        </ChatContainer.Messages>
      );
    };

    const renderInputArea = () => {
      if (renderInput) {
        return renderInput({
          value: inputValue,
          onChange: setInputValue,
          onSubmit: (message?: string) => handleSubmit(message),
          isLoading: isStreaming,
          onStop: stopStreaming,
          disabled: false,
        });
      }

      return (
        <ChatContainer.Input>
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSubmit={(message?: string) => handleSubmit(message)}
            placeholder={placeholder}
            disabled={false}
            isLoading={isStreaming}
            loadingIcon="square"
            onStopLoading={stopStreaming}
            inputRef={inputRef}
            inactiveOpacity={false}
          />
        </ChatContainer.Input>
      );
    };

    return (
      <ChatContainer layout={layout} isInPanel={isInPanel} isInTab={isInTab}>
        {renderMessages()}
        {isInTab && <div className="flex-1" />}
        {renderInputArea()}
      </ChatContainer>
    );
  },
);

StreamingChatContainer.displayName = 'StreamingChatContainer';
